import { Component, forwardRef, Input, Output, EventEmitter, ChangeDetectionStrategy, ViewChild, ElementRef, signal, computed, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';


export type RmsUploadValue = RmsUploadItem[];


export interface RmsUploadItem {
  name: string;
  size: number;
  type: string;
  lastModified?: number;
  file?: File;
  url?: string;
  previewUrl?: string;
  progress?: number;
  error?: string | null;
}


export type UploaderFn = (file: File, onProgress: (p: number) => void) => Promise<{ url?: string } | void>;


@Component({
  selector: 'rms-uploader',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './rms-uploader.component.html',
  styleUrls: ['./rms-uploader.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => RmsUploaderComponent),
    multi: true
  }]
})
export class RmsUploaderComponent implements ControlValueAccessor {
  @Input() label = 'Documents';
  @Input() hint?: string;
  @Input() placeholder = 'Drop files here or click to browse';
  @Input() multiple = true;
  @Input() accept = '';
  @Input() required = false;
  @Input() disabled = false;
  @Input() maxFiles = 10;
  @Input() maxSizeMB = 25;
  @Input() autoUpload = false;
  @Input() showPreview = true;
  @Input() uploader?: UploaderFn;


  @Output() filesChange = new EventEmitter<RmsUploadValue>();
  @Output() fileUploaded = new EventEmitter<RmsUploadItem>();
  @Output() fileRemoved = new EventEmitter<RmsUploadItem>();


  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;


  readonly items = signal<RmsUploadItem[]>([]);
  readonly dragging = signal(false);
  readonly errorMsg = signal<string | null>(null);


  readonly count = computed(() => this.items().length);
  readonly hasError = computed(() => !!this.errorMsg());
  readonly isAtLimit = computed(() => this.items().length >= this.maxFiles);


  private _onChange?: (val: RmsUploadValue) => void;
  private _onTouched?: () => void;


  constructor() {
    effect(() => {
      this.filesChange.emit(this.items());
      this._onChange?.(this.items());
      this._onTouched?.();
    });
  }
  writeValue(value: RmsUploadValue | null): void {
    if (!value) { this.items.set([]); return; }
    this.items.set(value.map(v => ({ ...v, progress: v.progress ?? (v.file ? undefined : 100) })));
  }
  registerOnChange(fn: any): void { this._onChange = fn; }
  registerOnTouched(fn: any): void { this._onTouched = fn; }
  setDisabledState(isDisabled: boolean): void { this.disabled = isDisabled; }


  openFileDialog(): void {
    if (this.disabled || this.isAtLimit()) return;
    this.fileInput?.nativeElement.click();
  }


  onBrowseChange(ev: Event) {
    const input = ev.target as HTMLInputElement;
    const files = input.files ? Array.from(input.files) : [];
    if (files.length) {
      this.addFiles(files);
      input.value = '';
    }
  }


  onDrop(ev: DragEvent) {
    ev.preventDefault();
    this.dragging.set(false);
    if (this.disabled) return;
    const dt = ev.dataTransfer;
    if (!dt) return;
    const dropped = dt.files ? Array.from(dt.files) : [];
    if (dropped.length) this.addFiles(dropped);
  }
  onDragOver(ev: DragEvent) { ev.preventDefault(); if (!this.disabled) this.dragging.set(true); }
  onDragLeave() { this.dragging.set(false); }


  remove(item: RmsUploadItem) {
    if (this.disabled) return;
    const next = this.items().filter(i => i !== item);
    this.items.set(next);
    this.fileRemoved.emit(item);
  }


  async upload(item: RmsUploadItem) {
    if (!this.uploader || !item.file) return;
    item.progress = 0;
    try {
      const res = await this.uploader(item.file, (p) => {
        item.progress = Math.min(99, Math.max(0, p));
        this.items.set([...this.items()]);
      });
      if (res && 'url' in res && res.url) item.url = res.url;
      item.progress = 100;
      item.error = null;
      this.items.set([...this.items()]);
      this.fileUploaded.emit(item);
    } catch (e: any) {
      item.error = (e?.message ?? 'Upload failed');
      item.progress = 0;
      this.items.set([...this.items()]);
    }
  }


  async uploadAll() {
    if (!this.uploader) return;
    for (const it of this.items()) {
      if (it.file && it.progress !== 100) {
        await this.upload(it);
      }
    }
  }
  private addFiles(files: File[]) {
    const current = this.items();
    const room = Math.max(0, this.maxFiles - current.length);
    const take = this.multiple ? files.slice(0, room) : files.slice(0, Math.min(1, room));


    const violations: string[] = [];
    const accepted = this.filterByAccept(take, violations);
    const sized = this.filterBySize(accepted, violations);


    if (violations.length) {
      this.errorMsg.set(violations.join('\n'));
      setTimeout(() => this.errorMsg.set(null), 5000);
    }


    const toAdd: RmsUploadItem[] = [];
    sized.forEach(f => toAdd.push({
      name: f.name,
      size: f.size,
      type: f.type,
      lastModified: f.lastModified,
      file: f,
      progress: undefined,
      error: null
    }));


    if (this.showPreview) {
      toAdd.forEach(item => {
        if (item.file && (item.type.startsWith('image/') || item.type === 'application/pdf')) {
          const reader = new FileReader();
          reader.onload = () => { item.previewUrl = String(reader.result); this.items.set([...this.items(), ...[]]); };
          reader.readAsDataURL(item.file);
        }
      });
    }


    this.items.set([...current, ...toAdd]);


    if (this.uploader && this.autoUpload) {
      (async () => { for (const i of toAdd) await this.upload(i); })();
    }
  }


  private filterBySize(files: File[], violations: string[]): File[] {
    const max = this.maxSizeMB * 1024 * 1024;
    return files.filter(f => {
      const ok = f.size <= max;
      if (!ok) violations.push(`${f.name}: exceeds ${this.maxSizeMB}MB`);
      return ok;
    });
  }
  private filterByAccept(files: File[], violations: string[]): File[] {
    if (!this.accept?.trim()) return files;
    const tokens = this.accept.split(',').map(t => t.trim()).filter(Boolean);


    const matches = (file: File) => {
      return tokens.some(t => {
        if (t === '*/*') return true;
        if (t.endsWith('/*')) return file.type.startsWith(t.slice(0, -1));
        if (t.startsWith('.')) return file.name.toLowerCase().endsWith(t.toLowerCase());
        return file.type === t;
      });
    };


    return files.filter(f => {
      const ok = matches(f);
      if (!ok) violations.push(`${f.name}: type not allowed`);
      return ok;
    });
  }
}
